<template>
  <v-card class="show profile elevation-4" v-if="profile">
    <v-card-title primary-title>
      <v-layout>
        <AvatarUpload
          :src="profile.avatar"
          :path="`/admin/profiles/${profile.id}/avatar`"
          @success="avatarSuccess"
        />

        <div>
          <div class="headline" v-if="profile.firstNames">
            {{ profile.firstNames }} {{ profile.lastName }}
          </div>
          <div v-else class="headline"><em>Unnamed Profile</em></div>
          <div class="subtitle" v-if="profile.countryOfBirth">
            <span :class="`flag-icon flag-icon-${profile.countryOfBirth.toLowerCase()}`" />
            {{ profile.identification }}
          </div>
        </div>
      </v-layout>
      <v-spacer />

      <div>
        <p class="text-xs-right ma-0">
          <v-tooltip left v-if="!profile.complete">
            <v-chip slot="activator">
              <v-icon left>mdi-alert-circle</v-icon>
              Incomplete
            </v-chip>
            <span>
              Profile requires vital information
              <ul>
                <li v-for="(action, i) in profile.actionItems" :key="i">
                  {{ action.type.split('::')[1] }}
                </li>
              </ul>
            </span>
          </v-tooltip>
          <v-tooltip v-if="profile.verifiedAt" left>
            <v-btn small flat fab slot="activator" color="success lighten-1">
              <v-icon>mdi-check</v-icon>
            </v-btn>
            <span>
              Verified
              {{ new Date(profile.verifiedAt) | inWords({ addSuffix: true }) }}
            </span>
          </v-tooltip>
        </p>
        <p class="text-xs-right pr-2 ma-0" v-if="profile.flags && profile.flags[0]">
          <Flag icon v-for="(flag, i) in profile.flags" :key="`flag-${i}`" :value="flag" />
        </p>
      </div>
    </v-card-title>

    <v-divider />
    <v-card-text v-if="profile.biography">
      <Markdown :html="false" :source="profile.biography" class="markdown" />
    </v-card-text>
    <v-divider v-if="profile.biography" />

    <table class="v-table theme--dark">
      <tbody>
        <tr>
          <th class="text-xs-right">ID</th>
          <td>{{ profile.id }}</td>
        </tr>
        <tr>
          <th class="text-xs-right">Candidate</th>
          <td>
            <a
              v-if="profile.candidate && profile.candidate.id"
              :href="`/candidates/${profile.candidate.id}`"
            >
              {{ profile.candidate.email }}
            </a>
          </td>
        </tr>
        <tr>
          <th class="text-xs-right">First Names</th>
          <td>{{ profile.firstNames }}</td>
        </tr>
        <tr>
          <th class="text-xs-right">Last Name</th>
          <td>{{ profile.lastName }}</td>
        </tr>
        <tr>
          <th class="text-xs-right">Identification</th>
          <td>
            <a v-if="profile.identityDocument" target="_blank" :href="profile.identityDocument.url">
              {{ profile.identification }}
            </a>
            <span v-else>{{ profile.identification }}</span>
          </td>
        </tr>
        <tr>
          <th class="text-xs-right">Nationality</th>
          <td>{{ profile.countryOfBirth }}</td>
        </tr>
        <tr>
          <th class="text-xs-right">Gender</th>
          <td v-if="profile.gender == ''"><em>Unknown</em></td>
          <td v-else>{{ profile.gender }}</td>
        </tr>
        <tr>
          <th class="text-xs-right">Ethnicity</th>
          <td v-if="profile.ethnicity">{{ profile.ethnicity }}</td>
          <td v-else><em>Unknown</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Disability</th>
          <td v-if="profile.disabled == true">
            <v-chip v-if="profile.disabilityCategory">{{
              disabilityCategories[profile.disabilityCategory]
            }}</v-chip>
            <v-chip v-else>Unspecified</v-chip>
          </td>
          <td v-else-if="profile.disabled === false">None</td>
          <td v-else><em>Unknown</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Date of Birth</th>
          <td v-if="profile.bornAt">
            {{ new Date(profile.bornAt) | date('d MMMM yyyy') }}
            ({{ new Date(profile.bornAt) | inWords }})
          </td>
          <td v-else><em>Unknown</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Drivers License</th>
          <td>{{ profile.driversLicense ? 'Yes' : 'No' }}</td>
        </tr>
        <tr>
          <th class="text-xs-right">Mobile No</th>
          <td v-if="profile.mobileNumber">{{ profile.mobileNumber | phone }}</td>
          <td v-else><em>Unknown</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Location</th>
          <td v-if="profile.location">{{ profile.location.address }}</td>
          <td v-else><em>Unknown</em></td>
        </tr>
        <tr v-if="profile.interests">
          <th class="text-xs-right">Interests</th>
          <td v-if="profile.interests.length > 0">
            <v-chip v-for="interest in profile.interests" :key="interest">{{ interest }}</v-chip>
          </td>
          <td v-else><em>None</em></td>
        </tr>
        <tr v-if="profile.behavioralCompetencies">
          <th class="text-xs-right">Personal Traits</th>
          <td v-if="profile.behavioralCompetencies.length > 0">
            <v-dialog lazy v-for="(trait, i) in profile.behavioralCompetencies" :key="`trait-${i}`">
              <v-chip slot="activator">{{ trait.name }}</v-chip>
              <v-card dark
                ><v-card-text>{{ trait.description }}</v-card-text></v-card
              >
            </v-dialog>
          </td>
          <td v-else><em>None</em></td>
        </tr>
        <tr v-if="profile.technicalCompetencies">
          <th class="text-xs-right">Technical Competencies</th>
          <td v-if="profile.technicalCompetencies.length > 0">
            <v-chip v-for="competency in profile.technicalCompetencies" :key="competency">{{
              competency
            }}</v-chip>
          </td>
          <td v-else><em>None</em></td>
        </tr>
        <tr v-if="profile.professionalTools">
          <th class="text-xs-right">Professional Tools</th>
          <td v-if="profile.professionalTools.length > 0">
            <v-chip v-for="tool in profile.professionalTools" :key="tool">{{ tool }}</v-chip>
          </td>
          <td v-else><em>None</em></td>
        </tr>
        <tr>
          <th class="text-xs-right">Profile Qualifications</th>
          <td>{{ profile.profileQualificationsCount }}</td>
        </tr>
        <tr></tr>
        <tr>
          <th class="text-xs-right">Profile Completion</th>
          <td>{{ Math.round(profile.completion) }} %</td>
        </tr>
        <tr>
          <th class="text-xs-right">Receive Digests</th>
          <td>{{ profile.receiveDigests ? 'Yes' : 'No' }}</td>
        </tr>
        <tr>
          <th class="text-xs-right">Last updated</th>
          <td>
            {{ new Date(profile.updatedAt) | date('h:mm a, MMMM do yyyy') }}
            ({{ new Date(profile.updatedAt) | inWords({ addSuffix: true }) }})
          </td>
        </tr>
        <tr>
          <th class="text-xs-right">Created</th>
          <td>
            {{ new Date(profile.createdAt) | date('h:mm a, MMMM do yyyy') }}
            ({{ new Date(profile.createdAt) | inWords({ addSuffix: true }) }})
          </td>
        </tr>
      </tbody>
    </table>

    <v-divider />

    <v-card-actions
      ><v-spacer />
      <can I="delete" a="Profile">
        <v-dialog v-model="dialog" max-width="600">
          <v-btn flat slot="activator" color="error">
            <v-icon left>mdi-delete</v-icon> Delete
          </v-btn>

          <v-card>
            <v-card-title class="headline">
              Delete administrator
            </v-card-title>
            <v-card-text>
              This action cannot be undone and will remove all data associated to the profile. It is
              not recommended for any profile created by a candidate.
            </v-card-text>
            <v-card-actions>
              <v-btn flat block @click.native="dialog = false">cancel</v-btn>
              <v-btn block color="error" @click="destroy">Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </can>

      <can I="edit" a="Profile">
        <v-btn @click.prevent="$emit('edit')" color="primary">
          <v-icon left>mdi-pencil</v-icon> Edit
        </v-btn>
      </can>
    </v-card-actions>
  </v-card>
</template>

<script>
// Components
import AvatarUpload from '@/components/AvatarUpload'
import Flag from '@/components/flags/Flag'
import Markdown from 'vue-markdown'

// Data
import disabilityCategories from '@/data/disability_categories'

// Helpers
import { mapGetters } from 'vuex'

// Vue Component
export default {
  components: { AvatarUpload, Flag, Markdown },
  data() {
    return { dialog: false, disabilityCategories }
  },
  filters: {
    phone(number) {
      return number.length ? number.replace(/^0+/, '+') : ''
    },
  },
  computed: mapGetters({ profile: 'admin/profiles/find' }),
  methods: {
    destroy() {
      this.$emit('destroy')
    },
    avatarSuccess() {
      this.$store.commit('snackbar', { text: 'Avatar uploaded!', type: 'success' })
      this.$store.dispatch('admin/profiles/fetch', this.profile.id)
    },
  },
}
</script>
